import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Connects a Google account for Gmail send + Calendar sync. Used on both the
// practitioner dashboard (their own mailbox/calendar) and the admin
// dashboard (the shared/domain mailbox + central bookings calendar).
const GoogleIntegrationCard = ({ apiUrl, token, title, description }) => {
  const [status, setStatus] = useState(null);
  const [calendarId, setCalendarId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('google');
    if (result === 'connected') setNotice('Google account connected.');
    if (result === 'error') setError('Could not connect that Google account. Please try again.');
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/integrations/google/status`, { headers });
      setStatus(res.data);
      setCalendarId(res.data.calendar_id || 'primary');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load Google connection status');
    } finally {
      setLoading(false);
    }
  };

  const connect = async () => {
    try {
      const res = await axios.get(`${apiUrl}/integrations/google/connect`, { headers });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start Google connection');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect this Google account? Calendar sync and email sending from it will stop.')) return;
    try {
      await axios.post(`${apiUrl}/integrations/google/disconnect`, {}, { headers });
      setStatus({ connected: false });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disconnect');
    }
  };

  const saveCalendar = async () => {
    try {
      const res = await axios.put(`${apiUrl}/integrations/google/calendar`, { calendar_id: calendarId }, { headers });
      setNotice(`Events will be written to calendar "${res.data.calendar_id}".`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update calendar');
    }
  };

  if (loading) return null;

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div className="card-header">{title || 'Google Calendar & Gmail'}</div>
      {description && <p className="text-muted" style={{ marginBottom: 12 }}>{description}</p>}
      {error && <div className="error-message">{error}</div>}
      {notice && !error && <p className="text-muted" style={{ marginBottom: 12 }}>{notice}</p>}

      {status?.connected ? (
        <>
          <p style={{ marginBottom: 12 }}>
            Connected as <strong>{status.google_email}</strong>
          </p>
          <div className="form-group">
            <label>Calendar ID</label>
            <input
              type="text"
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
              placeholder="primary"
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={saveCalendar}>Save Calendar</button>
            <button className="btn btn-danger" onClick={disconnect}>Disconnect</button>
          </div>
        </>
      ) : (
        <button className="btn btn-primary" onClick={connect}>Connect Google Account</button>
      )}
    </div>
  );
};

export default GoogleIntegrationCard;
